import React, { useState, useEffect, useRef } from 'react';
import { pageAPI, infoscreenAPI } from '../api/endpoints';
import './PagesManager.css';

// Absolute origin for /uploads assets. In production REACT_APP_API_URL is the
// relative "/api", so fall back to the page origin (not localhost) for thumbnails.
const rawApiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';
const API_BASE = /^https?:\/\//i.test(rawApiUrl)
  ? rawApiUrl.replace(/\/api\/?$/, '')
  : window.location.origin;

interface PageFile {
  filename: string;
  originalName: string;
  mimeType: string;
  size: number;
  path: string;
  order: number;
  rotation?: number;
}

interface Page {
  _id: string;
  title: string;
  description: string;
  type: 'files' | 'url' | 'html' | 'infoscreen';
  url?: string;
  htmlContent?: string;
  infoscreenId?: any;
  files: PageFile[];
  duration: number;
  transition: string;
  startDate?: string | null;
  endDate?: string | null;
  createdAt: string;
}

// datetime-local <-> ISO helpers. datetime-local has no timezone info and its native
// picker defaults the time portion to 00:00 until the user touches it, which is
// exactly the "00:00 unless specified" behaviour we want for free.
const toDatetimeLocal = (iso?: string | null) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};
const fromDatetimeLocal = (value: string) => (value ? new Date(value).toISOString() : '');
const formatDateShort = (iso?: string | null) => {
  if (!iso) return null;
  const d = new Date(iso);
  return isNaN(d.getTime()) ? null : d.toLocaleString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};
const isExpired = (page: Page) => !!page.endDate && new Date(page.endDate).getTime() < Date.now();

interface InfoscreenOption {
  _id: string;
  name: string;
}

export const PagesManager: React.FC = () => {
  const [pages, setPages] = useState<Page[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [duration, setDuration] = useState(10);
  const [transition, setTransition] = useState('slide');
  const [uploading, setUploading] = useState(false);
  const [pageType, setPageType] = useState<'files' | 'url' | 'html' | 'infoscreen'>('files');
  const [url, setUrl] = useState('');
  const [htmlContent, setHtmlContent] = useState('');
  const [infoscreenId, setInfoscreenId] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [infoscreenOptions, setInfoscreenOptions] = useState<InfoscreenOption[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const addFileInputRef = useRef<HTMLInputElement>(null);
  const [addFilesPageId, setAddFilesPageId] = useState<string | null>(null);

  const [editingPageId, setEditingPageId] = useState<string | null>(null);
  const [editTitle, setEditTitle] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [editDuration, setEditDuration] = useState(10);
  const [editTransition, setEditTransition] = useState('slide');
  const [editPageType, setEditPageType] = useState<'files' | 'url' | 'html' | 'infoscreen'>('files');
  const [editUrl, setEditUrl] = useState('');
  const [editHtmlContent, setEditHtmlContent] = useState('');
  const [editInfoscreenId, setEditInfoscreenId] = useState('');
  const [editStartDate, setEditStartDate] = useState('');
  const [editEndDate, setEditEndDate] = useState('');
  const [savingEdit, setSavingEdit] = useState(false);

  useEffect(() => {
    fetchPages();
    infoscreenAPI.getAll().then(res => setInfoscreenOptions(res.data.data)).catch(console.error);
  }, []);

  const fetchPages = async () => {
    try {
      const response = await pageAPI.getAll();
      setPages(response.data.data);
    } catch (error) {
      console.error('Failed to fetch pages:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!title) return;
    if (pageType === 'url' && !url) return;

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('title', title);
      formData.append('description', description);
      formData.append('duration', String(duration));
      formData.append('transition', transition);
      formData.append('type', pageType);
      formData.append('startDate', fromDatetimeLocal(startDate));
      formData.append('endDate', fromDatetimeLocal(endDate));

      if (pageType === 'url') {
        formData.append('url', url);
      } else if (pageType === 'html') {
        formData.append('htmlContent', htmlContent);
      } else if (pageType === 'infoscreen') {
        formData.append('infoscreenId', infoscreenId);
      } else if (fileInputRef.current?.files) {
        for (let i = 0; i < fileInputRef.current.files.length; i++) {
          formData.append('files', fileInputRef.current.files[i]);
        }
      }

      await pageAPI.create(formData);
      setTitle('');
      setDescription('');
      setDuration(10);
      setTransition('slide');
      setPageType('files');
      setUrl('');
      setHtmlContent('');
      setInfoscreenId('');
      setStartDate('');
      setEndDate('');
      setShowCreate(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
      fetchPages();
    } catch (error: any) {
      alert('Fehler: ' + (error.response?.data?.message || error.message));
    } finally {
      setUploading(false);
    }
  };

  const handleAddFiles = async (pageId: string) => {
    if (!addFileInputRef.current?.files?.length) return;

    setUploading(true);
    try {
      const formData = new FormData();
      for (let i = 0; i < addFileInputRef.current.files.length; i++) {
        formData.append('files', addFileInputRef.current.files[i]);
      }
      await pageAPI.uploadFiles(pageId, formData);
      setAddFilesPageId(null);
      if (addFileInputRef.current) addFileInputRef.current.value = '';
      fetchPages();
    } catch (error: any) {
      alert('Fehler: ' + (error.response?.data?.message || error.message));
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteFile = async (pageId: string, fileIndex: number) => {
    if (!window.confirm('Datei wirklich löschen?')) return;
    try {
      await pageAPI.deleteFile(pageId, fileIndex);
      fetchPages();
    } catch (error) {
      alert('Fehler beim Löschen der Datei');
    }
  };

  const handleRotateFile = async (pageId: string, fileIndex: number) => {
    try {
      await pageAPI.rotateFile(pageId, fileIndex);
      fetchPages();
    } catch (error) {
      alert('Fehler beim Drehen des Bildes');
    }
  };

  const openEdit = (page: Page) => {
    setAddFilesPageId(null);
    setEditingPageId(page._id);
    setEditTitle(page.title);
    setEditDescription(page.description || '');
    setEditDuration(page.duration);
    setEditTransition(page.transition);
    setEditPageType(page.type);
    setEditUrl(page.url || '');
    setEditHtmlContent(page.htmlContent || '');
    setEditInfoscreenId(page.infoscreenId?._id || page.infoscreenId || '');
    setEditStartDate(toDatetimeLocal(page.startDate));
    setEditEndDate(toDatetimeLocal(page.endDate));
  };

  const handleUpdate = async (e: React.FormEvent, pageId: string) => {
    e.preventDefault();
    if (!editTitle) return;

    setSavingEdit(true);
    try {
      const data: any = {
        title: editTitle,
        description: editDescription,
        duration: editDuration,
        transition: editTransition,
        type: editPageType,
        startDate: fromDatetimeLocal(editStartDate),
        endDate: fromDatetimeLocal(editEndDate),
      };
      if (editPageType === 'url') data.url = editUrl;
      if (editPageType === 'html') data.htmlContent = editHtmlContent;
      if (editPageType === 'infoscreen') data.infoscreenId = editInfoscreenId;

      await pageAPI.update(pageId, data);
      setEditingPageId(null);
      fetchPages();
    } catch (error: any) {
      alert('Fehler: ' + (error.response?.data?.message || error.message));
    } finally {
      setSavingEdit(false);
    }
  };

  const handleDeletePage = async (pageId: string) => {
    if (!window.confirm('Seite und alle Dateien wirklich löschen?')) return;
    try {
      await pageAPI.delete(pageId);
      fetchPages();
    } catch (error) {
      alert('Fehler beim Löschen der Seite');
    }
  };

  const formatSize = (bytes: number) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  return (
    <div className="pages-manager">
      <div className="pages-header">
        <h2>Seiten</h2>
        <button className="btn-primary" onClick={() => setShowCreate(!showCreate)}>
          {showCreate ? 'Abbrechen' : '+ Neue Seite'}
        </button>
      </div>

      {showCreate && (
        <form className="create-form" onSubmit={handleCreate}>
          <div className="form-row">
            <div className="form-group">
              <label>Titel *</label>
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                required
                placeholder="z.B. Willkommensseite"
              />
            </div>
            <div className="form-group">
              <label>Dauer (Sekunden)</label>
              <input
                type="number"
                value={duration}
                onChange={(e) => setDuration(parseInt(e.target.value))}
                min={1}
              />
            </div>
            <div className="form-group">
              <label>Übergang</label>
              <select value={transition} onChange={(e) => setTransition(e.target.value)}>
                <option value="slide">Slide</option>
                <option value="fade">Fade</option>
                <option value="convex">Convex</option>
                <option value="concave">Concave</option>
                <option value="zoom">Zoom</option>
                <option value="none">Ohne</option>
              </select>
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label>Sichtbar ab</label>
              <input
                type="datetime-local"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
              />
            </div>
            <div className="form-group">
              <label>Sichtbar bis</label>
              <input
                type="datetime-local"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
              />
              <p className="empty-state" style={{ fontSize: 11, marginTop: 4 }}>Leer lassen = läuft nie ab.</p>
            </div>
          </div>
          <div className="form-group">
            <label>Beschreibung</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Optionale Beschreibung"
              rows={2}
            />
          </div>
          <div className="form-group">
            <label>Seitentyp</label>
            <div className="type-toggle">
              <button
                type="button"
                className={`toggle-btn ${pageType === 'files' ? 'active' : ''}`}
                onClick={() => setPageType('files')}
              >
                📁 Dateien (PDF, Bilder)
              </button>
              <button
                type="button"
                className={`toggle-btn ${pageType === 'url' ? 'active' : ''}`}
                onClick={() => setPageType('url')}
              >
                🌐 Webseite (URL)
              </button>
              <button
                type="button"
                className={`toggle-btn ${pageType === 'html' ? 'active' : ''}`}
                onClick={() => setPageType('html')}
              >
                &lt;/&gt; HTML
              </button>
              <button
                type="button"
                className={`toggle-btn ${pageType === 'infoscreen' ? 'active' : ''}`}
                onClick={() => setPageType('infoscreen')}
              >
                📺 Infoscreen
              </button>
            </div>
          </div>
          {pageType === 'infoscreen' ? (
            <div className="form-group">
              <label>Infoscreen-Konfiguration *</label>
              <select value={infoscreenId} onChange={e => setInfoscreenId(e.target.value)}>
                <option value="">-- Infoscreen wählen --</option>
                {infoscreenOptions.map(is => (
                  <option key={is._id} value={is._id}>{is.name}</option>
                ))}
              </select>
              {infoscreenOptions.length === 0 && (
                <p className="empty-state" style={{fontSize:12,marginTop:4}}>Erstelle zuerst einen Infoscreen unter "Infoscreens".</p>
              )}
            </div>
          ) : pageType === 'html' ? (
            <div className="form-group">
              <label>HTML-Code</label>
              <textarea
                value={htmlContent}
                onChange={(e) => setHtmlContent(e.target.value)}
                placeholder={'<div style="...">\n  Dein HTML hier...\n</div>'}
                rows={12}
                className="html-editor"
                spellCheck={false}
              />
            </div>
          ) : pageType === 'url' ? (
            <div className="form-group">
              <label>URL (https://...)</label>
              <input
                type="url"
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                placeholder="https://example.com"
                required
              />
            </div>
          ) : (
            <div className="form-group">
              <label>Dateien (PNG, JPEG, PDF, ...)</label>
              <input
                type="file"
                ref={fileInputRef}
                multiple
                accept="image/png,image/jpeg,image/gif,image/webp,image/svg+xml,application/pdf"
              />
            </div>
          )}
          <button type="submit" className="btn-primary" disabled={uploading || !title || (pageType === 'url' && !url) || (pageType === 'html' && !htmlContent) || (pageType === 'infoscreen' && !infoscreenId)}>
            {uploading ? 'Wird hochgeladen...' : 'Seite erstellen'}
          </button>
        </form>
      )}

      {loading ? (
        <p>Laden...</p>
      ) : pages.length === 0 ? (
        <p className="empty-state">Noch keine Seiten erstellt. Klicke auf "+ Neue Seite".</p>
      ) : (
        <div className="pages-list">
          {pages.map((page) => (
            <div key={page._id} className="page-card">
              <div className="page-card-header">
                <div>
                  <h3>{page.title}</h3>
                  {page.description && <p className="page-desc">{page.description}</p>}
                  <div className="page-meta">
                    <span>{page.duration}s</span>
                    <span>Übergang: {page.transition}</span>
                    {page.type === 'url' ? (
                      <span className="type-badge url">🌐 {page.url}</span>
                    ) : page.type === 'html' ? (
                      <span className="type-badge html">&lt;/&gt; HTML</span>
                    ) : page.type === 'infoscreen' ? (
                      <span className="type-badge infoscreen">📺 {page.infoscreenId?.name || 'Infoscreen'}</span>
                    ) : (
                      <span>{page.files.length} Datei(en)</span>
                    )}
                    {(page.startDate || page.endDate) && (
                      <span className={`type-badge ${isExpired(page) ? 'expired' : 'scheduled'}`}>
                        {isExpired(page) ? '⏱ Abgelaufen · ' : '🗓 '}
                        {page.startDate ? formatDateShort(page.startDate) : 'sofort'}
                        {' → '}
                        {page.endDate ? formatDateShort(page.endDate) : 'unbegrenzt'}
                      </span>
                    )}
                  </div>
                </div>
                <div className="page-actions">
                  <button
                    className="btn-small btn-secondary"
                    onClick={() => (editingPageId === page._id ? setEditingPageId(null) : openEdit(page))}
                  >
                    {editingPageId === page._id ? 'Abbrechen' : 'Bearbeiten'}
                  </button>
                  <button
                    className="btn-small btn-secondary"
                    onClick={() => setAddFilesPageId(addFilesPageId === page._id ? null : page._id)}
                  >
                    + Dateien
                  </button>
                  <button className="btn-small btn-danger" onClick={() => handleDeletePage(page._id)}>
                    Löschen
                  </button>
                </div>
              </div>

              {editingPageId === page._id && (
                <form className="create-form" onSubmit={(e) => handleUpdate(e, page._id)}>
                  <div className="form-row">
                    <div className="form-group">
                      <label>Titel *</label>
                      <input
                        type="text"
                        value={editTitle}
                        onChange={(e) => setEditTitle(e.target.value)}
                        required
                      />
                    </div>
                    <div className="form-group">
                      <label>Dauer (Sekunden)</label>
                      <input
                        type="number"
                        value={editDuration}
                        onChange={(e) => setEditDuration(parseInt(e.target.value))}
                        min={1}
                      />
                    </div>
                    <div className="form-group">
                      <label>Übergang</label>
                      <select value={editTransition} onChange={(e) => setEditTransition(e.target.value)}>
                        <option value="slide">Slide</option>
                        <option value="fade">Fade</option>
                        <option value="convex">Convex</option>
                        <option value="concave">Concave</option>
                        <option value="zoom">Zoom</option>
                        <option value="none">Ohne</option>
                      </select>
                    </div>
                  </div>
                  <div className="form-row">
                    <div className="form-group">
                      <label>Sichtbar ab</label>
                      <input
                        type="datetime-local"
                        value={editStartDate}
                        onChange={(e) => setEditStartDate(e.target.value)}
                      />
                    </div>
                    <div className="form-group">
                      <label>Sichtbar bis</label>
                      <input
                        type="datetime-local"
                        value={editEndDate}
                        onChange={(e) => setEditEndDate(e.target.value)}
                      />
                      <p className="empty-state" style={{ fontSize: 11, marginTop: 4 }}>Leer lassen = läuft nie ab.</p>
                    </div>
                  </div>
                  <div className="form-group">
                    <label>Beschreibung</label>
                    <textarea
                      value={editDescription}
                      onChange={(e) => setEditDescription(e.target.value)}
                      rows={2}
                    />
                  </div>
                  <div className="form-group">
                    <label>Seitentyp</label>
                    <div className="type-toggle">
                      <button
                        type="button"
                        className={`toggle-btn ${editPageType === 'files' ? 'active' : ''}`}
                        onClick={() => setEditPageType('files')}
                      >
                        📁 Dateien (PDF, Bilder)
                      </button>
                      <button
                        type="button"
                        className={`toggle-btn ${editPageType === 'url' ? 'active' : ''}`}
                        onClick={() => setEditPageType('url')}
                      >
                        🌐 Webseite (URL)
                      </button>
                      <button
                        type="button"
                        className={`toggle-btn ${editPageType === 'html' ? 'active' : ''}`}
                        onClick={() => setEditPageType('html')}
                      >
                        &lt;/&gt; HTML
                      </button>
                      <button
                        type="button"
                        className={`toggle-btn ${editPageType === 'infoscreen' ? 'active' : ''}`}
                        onClick={() => setEditPageType('infoscreen')}
                      >
                        📺 Infoscreen
                      </button>
                    </div>
                  </div>
                  {editPageType === 'infoscreen' ? (
                    <div className="form-group">
                      <label>Infoscreen-Konfiguration *</label>
                      <select value={editInfoscreenId} onChange={e => setEditInfoscreenId(e.target.value)}>
                        <option value="">-- Infoscreen wählen --</option>
                        {infoscreenOptions.map(is => (
                          <option key={is._id} value={is._id}>{is.name}</option>
                        ))}
                      </select>
                    </div>
                  ) : editPageType === 'html' ? (
                    <div className="form-group">
                      <label>HTML-Code</label>
                      <textarea
                        value={editHtmlContent}
                        onChange={(e) => setEditHtmlContent(e.target.value)}
                        rows={12}
                        className="html-editor"
                        spellCheck={false}
                      />
                    </div>
                  ) : editPageType === 'url' ? (
                    <div className="form-group">
                      <label>URL (https://...)</label>
                      <input
                        type="url"
                        value={editUrl}
                        onChange={(e) => setEditUrl(e.target.value)}
                        placeholder="https://example.com"
                        required
                      />
                    </div>
                  ) : (
                    <p className="empty-state" style={{ fontSize: 12 }}>
                      Dateien können über "+ Dateien" hinzugefügt werden.
                    </p>
                  )}
                  <button
                    type="submit"
                    className="btn-primary"
                    disabled={
                      savingEdit ||
                      !editTitle ||
                      (editPageType === 'url' && !editUrl) ||
                      (editPageType === 'html' && !editHtmlContent) ||
                      (editPageType === 'infoscreen' && !editInfoscreenId)
                    }
                  >
                    {savingEdit ? 'Wird gespeichert...' : 'Änderungen speichern'}
                  </button>
                </form>
              )}

              {addFilesPageId === page._id && page.type !== 'url' && (
                <div className="add-files-form">
                  <input
                    type="file"
                    ref={addFileInputRef}
                    multiple
                    accept="image/png,image/jpeg,image/gif,image/webp,image/svg+xml,application/pdf"
                  />
                  <button
                    className="btn-small btn-primary"
                    onClick={() => handleAddFiles(page._id)}
                    disabled={uploading}
                  >
                    Hochladen
                  </button>
                </div>
              )}

              {page.type === 'url' && page.url && (
                <div className="url-preview">
                  <iframe src={page.url} title={page.title} className="url-preview-frame" />
                </div>
              )}

              {page.type === 'html' && page.htmlContent && (
                <div className="html-preview">
                  <pre className="html-code">{page.htmlContent.substring(0, 500)}{page.htmlContent.length > 500 ? '…' : ''}</pre>
                </div>
              )}

              <div className="file-grid">
                {page.files
                  .sort((a, b) => a.order - b.order)
                  .map((file, index) => (
                    <div key={file.filename} className="file-item">
                      {file.mimeType.startsWith('image/') ? (
                        <img
                          src={`${API_BASE}${file.path}`}
                          alt={file.originalName}
                          className="file-preview"
                          style={{ transform: `rotate(${file.rotation || 0}deg)` }}
                        />
                      ) : (
                        <div className="file-preview file-icon">
                          <span>PDF</span>
                        </div>
                      )}
                      <div className="file-info">
                        <span className="file-name" title={file.originalName}>
                          {file.originalName}
                        </span>
                        <span className="file-size">
                          {formatSize(file.size)}{file.rotation ? ` · ${file.rotation}°` : ''}
                        </span>
                      </div>
                      <div className="file-actions">
                        {file.mimeType.startsWith('image/') && (
                          <button
                            className="btn-tiny"
                            onClick={() => handleRotateFile(page._id, index)}
                            title="90° drehen"
                          >
                            ↻
                          </button>
                        )}
                        <button
                          className="btn-tiny btn-danger"
                          onClick={() => handleDeleteFile(page._id, index)}
                          title="Datei löschen"
                        >
                          ✕
                        </button>
                      </div>
                    </div>
                  ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
